import type {
  MilestoneType,
  ResponsibleTeam,
  MilestoneCategory
} from '../types';

// Generate unique IDs
const generateId = () => Math.random().toString(36).substr(2, 9);

// Fixed milestone types based on blueprint specification
export const MOCK_MILESTONE_TYPES: MilestoneType[] = [
  {
    id: generateId(),
    code: 'DISPLAY_GROUP_ADJUSTMENT',
    name: 'Display Group Adjustment',
    description: 'Adjust Sub-Class associations of HQ Display Group, affecting product grouping logic',
    defaultResponsibleTeam: 'Planogram Team',
    defaultDuration: 3,
    defaultBufferTime: 1,
    canBeParallel: false,
    isFinalMilestone: false,
    category: 'Planning',
    prerequisites: [],
    deliverables: ['Updated Display Group configuration'],
    estimatedCost: 8000,
    requiredSkills: ['Display Group Management', 'Sub-Class Configuration', 'Product Grouping'],
    riskLevel: 'Medium'
  },
  {
    id: generateId(),
    code: 'STORE_GROUP_SETUP',
    name: 'Store Group Setup',
    description: 'Adjust store scope under Store Group, determining stores covered by the plan',
    defaultResponsibleTeam: 'Planogram Team',
    defaultDuration: 2,
    defaultBufferTime: 1,
    canBeParallel: false,
    isFinalMilestone: false,
    category: 'Planning',
    prerequisites: ['DISPLAY_GROUP_ADJUSTMENT'],
    deliverables: ['Determined target store list'],
    estimatedCost: 5000,
    requiredSkills: ['Store Management', 'Scope Planning', 'Target Analysis'],
    riskLevel: 'Low'
  },
  {
    id: generateId(),
    code: 'BENCHMARK_MASTER_DRAWING',
    name: 'Benchmark Master Drawing',
    description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
    defaultResponsibleTeam: 'Planogram Team',
    defaultDuration: 5,
    defaultBufferTime: 2,
    canBeParallel: false,
    isFinalMilestone: false,
    category: 'Design',
    prerequisites: ['STORE_GROUP_SETUP'],
    deliverables: ['Benchmark display diagram'],
    estimatedCost: 12000,
    requiredSkills: ['Nielsen Software', 'Display Design', 'Technical Drawing'],
    riskLevel: 'Medium'
  },
  {
    id: generateId(),
    code: 'STORE_LAYOUT_ADJUSTMENT',
    name: 'Store Layout Adjustment',
    description: 'Execute physical layout adjustments in stores',
    defaultResponsibleTeam: 'Construction Team',
    defaultDuration: 7,
    defaultBufferTime: 2,
    canBeParallel: true,
    isFinalMilestone: false,
    category: 'Execution',
    prerequisites: ['BENCHMARK_MASTER_DRAWING'],
    deliverables: ['Updated store layout'],
    estimatedCost: 35000,
    requiredSkills: ['Physical Layout', 'Store Construction', 'Space Management'],
    riskLevel: 'High'
  },
  {
    id: generateId(),
    code: 'FIXTURE_TYPE_SETUP',
    name: 'Fixture Type Set Up',
    description: 'Set up new fixture types in master data for use in display planning',
    defaultResponsibleTeam: 'Floor Plan Team',
    defaultDuration: 3,
    defaultBufferTime: 1,
    canBeParallel: true,
    isFinalMilestone: false,
    category: 'Preparation',
    prerequisites: ['BENCHMARK_MASTER_DRAWING'],
    deliverables: ['New fixture type configuration'],
    estimatedCost: 10000,
    requiredSkills: ['Fixture Management', 'Master Data Setup', 'Configuration Management'],
    riskLevel: 'Medium'
  },
  {
    id: generateId(),
    code: 'DISPLAY_PLANNING_ADJUSTMENT',
    name: 'Display Planning Adjustment',
    description: 'Adjust section numbering and Display Group associations',
    defaultResponsibleTeam: 'Floor Plan Team',
    defaultDuration: 4,
    defaultBufferTime: 1,
    canBeParallel: false,
    isFinalMilestone: false,
    category: 'Planning',
    prerequisites: ['FIXTURE_TYPE_SETUP', 'STORE_LAYOUT_ADJUSTMENT'],
    deliverables: ['Updated display planning'],
    estimatedCost: 8000,
    requiredSkills: ['Display Planning', 'Section Management', 'Association Configuration'],
    riskLevel: 'Medium'
  },
  {
    id: generateId(),
    code: 'FIXTURE_PROCUREMENT',
    name: 'Fixture Procurement',
    description: 'Generate fixture procurement requirements based on layout and display adjustments',
    defaultResponsibleTeam: 'Procurement Team',
    defaultDuration: 5,
    defaultBufferTime: 2,
    canBeParallel: false,
    isFinalMilestone: false,
    category: 'Preparation',
    prerequisites: ['DISPLAY_PLANNING_ADJUSTMENT'],
    deliverables: ['Purchase orders'],
    estimatedCost: 50000,
    requiredSkills: ['Procurement Management', 'Vendor Relations', 'Purchase Planning'],
    riskLevel: 'High'
  },
  {
    id: generateId(),
    code: 'DISPLAY_DIAGRAM_GENERATION',
    name: 'Algorithm Generated Planogram for Review',
    description: 'Generate store display diagrams at specified times and conduct review confirmation',
    defaultResponsibleTeam: 'System Auto',
    defaultDuration: 3,
    defaultBufferTime: 1,
    canBeParallel: false,
    isFinalMilestone: false,
    category: 'Review',
    prerequisites: ['FIXTURE_PROCUREMENT'],
    deliverables: ['Final display diagram'],
    estimatedCost: 10000,
    requiredSkills: ['Diagram Generation', 'Review Process', 'Quality Assurance'],
    riskLevel: 'Medium'
  },
  {
    id: generateId(),
    code: 'STORE_TASK_GENERATION',
    name: 'Store Task Generation',
    description: 'Generate final store display adjustment tasks and distribute for execution',
    defaultResponsibleTeam: 'System Auto',
    defaultDuration: 1,
    defaultBufferTime: 0,
    canBeParallel: false,
    isFinalMilestone: true,
    category: 'Completion',
    prerequisites: ['DISPLAY_DIAGRAM_GENERATION'],
    deliverables: ['Store execution tasks'],
    estimatedCost: 2000,
    requiredSkills: ['Task Generation', 'System Automation', 'Distribution Management'],
    riskLevel: 'Low'
  }
];

// Export milestone types by category
export const MILESTONE_TYPES_BY_CATEGORY: Record<MilestoneCategory, MilestoneType[]> = {
  'Planning': MOCK_MILESTONE_TYPES.filter(m => m.category === 'Planning'),
  'Design': MOCK_MILESTONE_TYPES.filter(m => m.category === 'Design'),
  'Preparation': MOCK_MILESTONE_TYPES.filter(m => m.category === 'Preparation'),
  'Execution': MOCK_MILESTONE_TYPES.filter(m => m.category === 'Execution'),
  'Review': MOCK_MILESTONE_TYPES.filter(m => m.category === 'Review'),
  'Completion': MOCK_MILESTONE_TYPES.filter(m => m.category === 'Completion')
};

// Export milestone types by responsible team
export const MILESTONE_TYPES_BY_TEAM: Record<ResponsibleTeam, MilestoneType[]> = {
  'Planogram Team': MOCK_MILESTONE_TYPES.filter(m => m.defaultResponsibleTeam === 'Planogram Team'),
  'Construction Team': MOCK_MILESTONE_TYPES.filter(m => m.defaultResponsibleTeam === 'Construction Team'),
  'Floor Plan Team': MOCK_MILESTONE_TYPES.filter(m => m.defaultResponsibleTeam === 'Floor Plan Team'),
  'Procurement Team': MOCK_MILESTONE_TYPES.filter(m => m.defaultResponsibleTeam === 'Procurement Team'),
  'Marketing Team': MOCK_MILESTONE_TYPES.filter(m => m.defaultResponsibleTeam === 'Marketing Team'),
  'Training Team': MOCK_MILESTONE_TYPES.filter(m => m.defaultResponsibleTeam === 'Training Team'),
  'Visual Team': MOCK_MILESTONE_TYPES.filter(m => m.defaultResponsibleTeam === 'Visual Team'),
  'System Auto': MOCK_MILESTONE_TYPES.filter(m => m.defaultResponsibleTeam === 'System Auto')
};

// Export final milestone
export const FINAL_MILESTONE_TYPE = MOCK_MILESTONE_TYPES.find(m => m.isFinalMilestone)!;